import Button from "@/components/Button/Button"
import TextboxInput from "@/components/Inputs/TextboxInput"
import { MODALS } from "@/constants/modals"
import {
  useAddFreeMaterialReviewMutation,
  useEditFreeMaterialReviewMutation,
} from "@/queries/mutations/freeMaterialsMutations"
import type { FreeMaterialReviewDTO } from "@/types/freeMaterialsTypes"
import { plusIcon } from "@/utils/variables"
import { useStore } from "@/zustand/store"
import { Form, Formik, useFormikContext } from "formik"
import { useState, useTransition } from "react"
import { useTranslation } from "react-i18next"
import { FaRegStarHalf, FaStarHalf } from "react-icons/fa6"
import { useParams } from "react-router"
import { useDebouncedCallback } from "use-debounce"

export default function FreeMaterialReviewForm() {
  const [highlightedScore, setHighlightedScore] = useState(0)

  const { t } = useTranslation()
  const { courseId } = useParams()

  const editModalState = useStore((store) => store.editModalState)
  const review = useStore(
    (store) => store.modalsStates[MODALS.freeMaterial.reviewsForm]
  )

  const reviewObj = typeof review === "object" ? review : undefined

  const addFreeMaterialReview = useAddFreeMaterialReviewMutation(
    Number(courseId)
  )

  const editFreeMaterialReview = useEditFreeMaterialReviewMutation(
    Number(courseId)
  )

  const mutation = reviewObj ? editFreeMaterialReview : addFreeMaterialReview

  return (
    <Formik
      initialValues={{
        id: reviewObj?.id || undefined,
        score: reviewObj?.score || 5,
        comment: reviewObj?.comment || "",
      }}
      enableReinitialize
      onSubmit={(values) => {
        mutation.mutate(values, {
          onSuccess: () => {
            editModalState(MODALS.freeMaterial.reviewsForm, undefined)
          },
        })
      }}
    >
      {() => {
        return (
          <Form className="row">
            <div className={`d-flex flex-column justify-content-end mt-4`}>
              <div className="mb-3">
                <p className="fs-6 fw-semibold mb-2">
                  {t("freeMaterialsPage.ratingLabel")}
                </p>
                <div className="d-flex gap-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      index={i}
                      key={`star-${i}`}
                      highlightedScore={highlightedScore}
                      setHighlightedScore={setHighlightedScore}
                    />
                  ))}
                </div>
              </div>

              <TextboxInput
                id="comment"
                name="comment"
                as="field"
                label={t("freeMaterialsPage.commentLabel")}
                placeholder={t("freeMaterialsPage.commentLabel")}
              />
              <div className="d-flex gap-3 align-self-end">
                <Button
                  onClick={() =>
                    editModalState(MODALS.freeMaterial.reviewsForm, undefined)
                  }
                  type="button"
                  variant={"normal"}
                >
                  {t("buttons.cancel")}
                </Button>

                <Button
                  type="submit"
                  isLoading={mutation.isPending}
                  icon={plusIcon}
                >
                  {t("buttons.add")}
                </Button>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

function Star({
  index,
  highlightedScore,
  setHighlightedScore,
}: {
  index: number
  highlightedScore: number
  setHighlightedScore: (score: number) => void
}) {
  const { values, setFieldValue } = useFormikContext<FreeMaterialReviewDTO>()

  const leftHighlighted =
    (values.score || 0) >= index + 0.5 || highlightedScore >= index + 0.5
  const rightHighlighted =
    (values.score || 0) >= index + 1 || highlightedScore >= index + 1

  const [, startTransition] = useTransition()

  const debouncedResetHighlightedScore = useDebouncedCallback(() => {
    setHighlightedScore(0)
  }, 500)

  const resetHighlightedScore = () => {
    startTransition(() => {
      debouncedResetHighlightedScore()
    })
  }

  return (
    <>
      <div className="d-flex gap-0">
        <div
          style={{
            width: "20px",
            overflow: "clip",
          }}
          onMouseEnter={() => setHighlightedScore(index + 0.5)}
          onMouseLeave={() => resetHighlightedScore()}
          onClick={() => setFieldValue("score", index + 0.5)}
        >
          <div style={{ transform: "scale(1.7) translateX(4px)" }}>
            {leftHighlighted ? (
              <FaStarHalf
                size={36}
                color="#f8c315"
                style={{
                  width: "20px",
                }}
              />
            ) : (
              <FaRegStarHalf
                size={36}
                style={{
                  width: "20px",
                }}
              />
            )}
          </div>
        </div>
        <div
          style={{
            width: "20px",
            overflow: "clip",
          }}
          onMouseEnter={() => setHighlightedScore(index + 1)}
          onMouseLeave={() => resetHighlightedScore()}
          onClick={() => setFieldValue("score", index + 1)}
        >
          <div style={{ transform: "scale(1.7)  translateX(-5px)" }}>
            {rightHighlighted ? (
              <FaStarHalf
                color="#f8c315"
                size={36}
                style={{
                  width: "20px",
                  transform: "scaleX(-1) ",
                  transformOrigin: "center",
                  display: "inline-block",
                }}
              />
            ) : (
              <FaRegStarHalf
                size={36}
                style={{
                  width: "20px",
                  transform: "scaleX(-1) ",
                  transformOrigin: "center",
                  display: "inline-block",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
